/**
 * Pure maths for the evaluation matrix and the progression engine.
 *
 * Every function here mirrors the Postgres implementation in
 * `infrastructure/supabase/migrations/20260101000001_init.sql` §7 exactly:
 *
 *   epley_1rm(w, r) = round(w * (1 + least(r, 12) / 30), 2)   -- half-up
 *   rep_bucket_for(r) = 1-3 | 4-6 | 7-10 | 11+
 *
 * Postgres `round(numeric, 2)` is half-away-from-zero, so a plain `Math.round`
 * is *not* equivalent for negative inputs; `round2` implements the half-up rule
 * explicitly. Weights and reps are non-negative in practice, but the maths must
 * still match the database bit-for-bit for every accepted input.
 */

/** Epley is only accurate up to this many reps; the SQL caps the ratio here. */
export const EPLEY_REP_CAP = 12;
/** `w * (1 + reps / 30)` — the 30 is Epley's denominator. */
export const EPLEY_DENOMINATOR = 30;
/** Display/storage precision used by `round(..., 2)` in Postgres. */
export const ROUND_DECIMALS = 2;

/** Half-up (away from zero) rounding to `decimals` places — matches Postgres numeric round. */
export function roundHalfUp(value: number, decimals = ROUND_DECIMALS): number {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** decimals;
  const scaled = value * factor;
  // Nudge towards the value before rounding so binary noise such as
  // 1.005 * 100 = 100.49999999999999 still rounds half-up like numeric(…,2).
  const adjusted = scaled + Math.sign(scaled) * Number.EPSILON * Math.abs(scaled);
  return Math.round(adjusted) / factor;
}

/** Convenience wrapper for the 2dp used throughout the packet and reports. */
export function round2(value: number): number {
  return roundHalfUp(value, ROUND_DECIMALS);
}

/**
 * `public.epley_1rm`. Returns 0 for null/zero/negative reps and caps reps at 12,
 * exactly like the SQL `case … least(p_reps, 12)` branch.
 */
export function epley1rm(weightKg: number, reps: number): number {
  if (!Number.isFinite(weightKg) || !Number.isFinite(reps)) return 0;
  if (reps <= 0 || weightKg <= 0) return 0;
  const effectiveReps = Math.min(reps, EPLEY_REP_CAP);
  return round2(weightKg * (1 + effectiveReps / EPLEY_DENOMINATOR));
}

/** `public.rep_bucket_for`. `null`/non-positive reps fall into `1-3`, as in SQL. */
export function repBucket(reps: number | null | undefined): '1-3' | '4-6' | '7-10' | '11+' {
  if (reps === null || reps === undefined || Number.isNaN(reps) || reps <= 3) return '1-3';
  if (reps <= 6) return '4-6';
  if (reps <= 10) return '7-10';
  return '11+';
}

/** Raw tonnage: `Σ weight × reps`. Non-finite terms are ignored. */
export function volumeLoad(
  sets: ReadonlyArray<{ weightKg: number; reps: number }>,
): number {
  let total = 0;
  for (const set of sets) {
    if (!Number.isFinite(set.weightKg) || !Number.isFinite(set.reps)) continue;
    total += set.weightKg * set.reps;
  }
  return round2(total);
}

/** Arithmetic mean of the finite values in `values`; 0 when there is nothing to average. */
export function mean(values: ReadonlyArray<number>): number {
  let sum = 0;
  let count = 0;
  for (const value of values) {
    if (!Number.isFinite(value)) continue;
    sum += value;
    count += 1;
  }
  return count === 0 ? 0 : round2(sum / count);
}

/** Least-squares slope of `ys` against evenly spaced x = 0..n-1 (per step). */
export function linearSlope(ys: ReadonlyArray<number>): number {
  const points = ys.filter((value) => Number.isFinite(value));
  const n = points.length;
  if (n < 2) return 0;
  const xMean = (n - 1) / 2;
  const yMean = points.reduce((sum, value) => sum + value, 0) / n;
  let numerator = 0;
  let denominator = 0;
  for (let index = 0; index < n; index += 1) {
    const dx = index - xMean;
    numerator += dx * ((points[index] ?? 0) - yMean);
    denominator += dx * dx;
  }
  return denominator === 0 ? 0 : roundHalfUp(numerator / denominator, 4);
}

/**
 * Acute : chronic workload ratio from per-week tonnages.
 * acute   = mean of the last `acuteWeeks` weeks
 * chronic = mean of all supplied weeks (the window is the chronic baseline here)
 * Returns 0 when the chronic mean is 0 (nothing to divide by), which callers treat
 * as "no signal" rather than as a low ratio.
 */
export function acuteChronicRatio(weeklyVolumes: ReadonlyArray<number>, acuteWeeks = 1): number {
  const weeks = weeklyVolumes.filter((value) => Number.isFinite(value));
  if (weeks.length === 0) return 0;
  const chronic = weeks.reduce((sum, value) => sum + value, 0) / weeks.length;
  if (chronic === 0) return 0;
  const effectiveAcute = Math.min(Math.max(acuteWeeks, 1), weeks.length);
  const acuteSlice = weeks.slice(weeks.length - effectiveAcute);
  const acute = acuteSlice.reduce((sum, value) => sum + value, 0) / acuteSlice.length;
  return round2(acute / chronic);
}

/** Signed percentage change from `from` to `to`; 0 when `from` is 0. */
export function percentChange(from: number, to: number): number {
  if (!Number.isFinite(from) || !Number.isFinite(to) || from === 0) return 0;
  return round2(((to - from) / from) * 100);
}

/**
 * Snap `value` to the nearest multiple of `increment` (half-up). The smallest
 * real plates in the product are 0.5 kg, so increments are always positive and
 * the result keeps 2dp to stay comparable with `numeric(6,2)`.
 */
export function roundToIncrement(value: number, increment: number): number {
  if (!Number.isFinite(value)) return 0;
  if (!Number.isFinite(increment) || increment <= 0) return round2(value);
  return round2(Math.round(value / increment) * increment);
}

/** Clamp helper used by the safety rails. */
export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(Math.max(value, min), max);
}
